import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, ChevronRight, CreditCard, Users, ClipboardList, Search, FileSignature, ClipboardCheck, Landmark, Key, AlertCircle, HelpCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

const CHRIS_PHOTO = "https://customer-assets.emergentagent.com/job_a0897f07-e635-4cd5-9eb9-21140ceaa71c/artifacts/drlh8cs0_brookebarclayphotography-1-99.jpg";

const TOC_ITEMS = [
  { id: "step-1", label: "Step 1: Getting Financing Ready", icon: CreditCard },
  { id: "step-2", label: "Step 2: Working With an Agent", icon: Users },
  { id: "step-3", label: "Step 3: Defining Your Criteria", icon: ClipboardList },
  { id: "step-4", label: "Step 4: Touring Homes", icon: Search },
  { id: "step-5", label: "Step 5: Making an Offer", icon: FileSignature },
  { id: "step-6", label: "Step 6: Inspection & Appraisal", icon: ClipboardCheck },
  { id: "step-7", label: "Step 7: Finalizing Financing", icon: Landmark },
  { id: "step-8", label: "Step 8: Closing", icon: Key },
  { id: "special-situations", label: "Special Situations", icon: AlertCircle },
  { id: "faq", label: "FAQ", icon: HelpCircle },
];

export default function BuyerGuidePage() {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Buying a Home in Grays Harbor: The Complete Guide | Chris Pierog</title>
        <meta name="description" content="Everything you need to know before you start touring homes, while you're under contract, and all the way through closing — written by Chris Pierog, Spivey Realty Group." />
        <meta name="keywords" content="buy home Grays Harbor, buying house Aberdeen WA, Grays Harbor real estate guide, home buying process, first time home buyer, Chris Pierog realtor" />
      </Helmet>

      <div className="min-h-screen bg-[#f0efeb] flex flex-col" data-testid="buyer-guide-page">
        <Header />
        
        <main className="flex-1 pt-20">
          {/* Hero Section */}
          <section className="bg-[#132723] text-white py-16 md:py-20">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <ChevronRight className="w-4 h-4" />
                <span>Buyer Guide</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif leading-tight mb-6">
                Buying a Home in Grays Harbor:<br />
                <span className="text-white/80">The Complete Guide</span>
              </h1>
              
              <p className="text-lg text-white/80 mb-8 max-w-3xl">
                {"Everything you need to know before you start touring homes, while you're under contract, and all the way through closing — written by Chris Pierog, Spivey Realty Group."}
              </p>

              <div className="flex items-center gap-4">
                <img 
                  src={CHRIS_PHOTO} 
                  alt="Chris Pierog" 
                  className="w-14 h-14 rounded-full object-cover object-top border-2 border-white/20"
                />
                <div>
                  <p className="font-medium">Chris Pierog</p>
                  <p className="text-sm text-white/60">Licensed Realtor® | Spivey Realty Group</p>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Overview */}
          <section className="bg-white py-12 border-b border-[#e2e5e2]">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <h2 className="text-2xl font-serif text-[#132723] mb-4">Quick Overview: The Buying Timeline</h2>
              <p className="text-[#4d6153] mb-6">
                Buying a home generally breaks into three phases:
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-[#f0efeb] p-4">
                  <p className="font-semibold text-[#132723] mb-1">Getting Ready</p>
                  <p className="text-sm text-[#4d6153]">Days to a few weeks — financing prep, working with an agent, and defining what you&apos;re looking for</p>
                </div>
                <div className="bg-[#f0efeb] p-4">
                  <p className="font-semibold text-[#132723] mb-1">The Search & Offer</p>
                  <p className="text-sm text-[#4d6153]">Varies widely — touring homes and writing a competitive offer once you find the right one</p>
                </div>
                <div className="bg-[#f0efeb] p-4">
                  <p className="font-semibold text-[#132723] mb-1">Under Contract to Closing</p>
                  <p className="text-sm text-[#4d6153]">~30 days — inspection, appraisal, financing finalization, and escrow</p>
                </div>
              </div>
              
              <p className="text-[#4d6153] text-sm">
                Below, we walk through every stage in detail. Jump to any section, or read start to finish.
              </p>
            </div>
          </section>

          {/* Table of Contents */}
          <section className="bg-[#f8f8f6] py-8 border-b border-[#e2e5e2] sticky top-16 md:top-20 z-30">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <p className="text-sm font-medium text-[#132723] mb-3">On this page:</p>
              <div className="flex flex-wrap gap-2">
                {TOC_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#e2e5e2] text-sm text-[#4d6153] hover:text-[#132723] hover:border-[#132723] transition-colors rounded-sm"
                  >
                    <item.icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{item.label}</span>
                    <span className="sm:hidden">{item.label.split(":")[0]}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Main Content */}
          <article className="py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              
              {/* Step 1 */}
              <section id="step-1" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 1: Getting Your Financing Ready
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    This is the step that decides whether you&apos;re a competitive buyer or not — and it&apos;s the one most people underestimate.
                  </p>
                  <div className="bg-[#132723] text-white p-6 my-6 -mx-2">
                    <p className="font-serif text-lg mb-2">Pre-qualified vs. Fully Underwritten</p>
                    <p className="text-white/80">
                      {"These are not the same thing. Pre-qualified means a lender took a quick look at self-reported info. Fully underwritten means your income, assets, and credit have actually been verified before you ever make an offer. In any market with competition, sellers can tell the difference, and an underwritten buyer looks like a sure thing while a pre-qualified buyer looks like a maybe."}
                    </p>
                  </div>
                  <p className="font-medium text-[#132723] mt-6 mb-3">Loan types to know:</p>
                  <ul className="space-y-3 list-none pl-0">
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Conventional</span> — typically requires stronger credit, down payments as low as 3-5% depending on the program
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">FHA</span> — lower down payment requirements (as low as 3.5%), more flexible credit standards, but comes with mortgage insurance
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">VA</span> — available to eligible veterans and service members, often with no down payment required
                    </li>
                  </ul>
                  <p className="text-sm text-[#4d6153]/70 mt-4 italic">
                    [Related: Getting fully underwritten before you shop — coming soon]<br />
                    [Related: Conventional vs. FHA vs. VA — which fits your situation — coming soon]
                  </p>
                </div>
              </section>

              {/* Step 2 */}
              <section id="step-2" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 2: Working With a Buyer&apos;s Agent
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    {"Under current Washington rules, buyers are required to sign a written buyer agency agreement before touring homes with an agent. This isn't a red flag — it's simply how representation now works statewide, and it protects you by clearly defining what your agent does for you and how they're compensated."}
                  </p>
                  <p>
                    {"In most transactions, buyer agent compensation is still commonly covered through the seller side of the deal, but it's a negotiated point in every transaction rather than an assumption."}
                  </p>
                  <p>
                    A buyer&apos;s agent gives you access to full local listing data, helps you evaluate homes objectively (not just emotionally), and negotiates on your behalf throughout the process.
                  </p>
                  <p className="text-sm text-[#4d6153]/70 mt-4 italic">
                    [Related: What a buyer agency agreement actually says — coming soon]
                  </p>
                </div>
              </section>

              {/* Step 3 */}
              <section id="step-3" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 3: Defining What You&apos;re Looking For
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>Before touring homes, get specific on:</p>
                  <ul className="space-y-3 list-none pl-0 my-6">
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Budget</span> — not just what you&apos;re approved for, but what payment you&apos;re actually comfortable with
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Must-haves vs. nice-to-haves</span> — bedrooms, bathrooms, lot size, garage, school zone
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Timeline</span> — are you flexible, or working around a lease end date or other deadline?
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Water/sewer preference</span> — many Grays Harbor properties are on private well and septic systems rather than city utilities; this is worth deciding upfront since it affects financing and inspection needs (see Special Situations)
                    </li>
                  </ul>
                </div>
              </section>

              {/* Step 4 */}
              <section id="step-4" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 4: Touring Homes
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    Once your criteria are set, your agent sets up showings for anything that fits. A few tips:
                  </p>
                  <ul className="space-y-2 list-disc pl-5 my-6">
                    <li><strong>Take notes and photos at every showing</strong> — homes blur together fast</li>
                    <li><strong>Pay attention to things that are expensive to change</strong> (layout, lot, location) vs. things that are easy to change (paint, fixtures, landscaping)</li>
                    <li><strong>Don&apos;t skip a home based on photos alone</strong> — some of the best deals are homes that photograph poorly but show beautifully in person</li>
                  </ul>
                </div>
              </section>

              {/* Step 5 */}
              <section id="step-5" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 5: Making an Offer
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>When you find the right home, your offer includes more than just price:</p>
                  <ul className="space-y-3 list-none pl-0 my-6">
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Earnest money</span> — a good-faith deposit, typically 1-3% of the purchase price (sometimes more in competitive situations), held by a neutral escrow or title company and credited toward your closing costs at the end. This is not an extra fee — it&apos;s a prepayment.
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Financing contingency</span> — protects your earnest money if you&apos;re unable to secure your loan
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Inspection contingency</span> — gives you the right to inspect the home and negotiate or exit based on findings
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Appraisal contingency</span> — protects you if the home appraises below the purchase price (see Special Situations)
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Closing timeline</span> — needs to work for both you and the seller
                    </li>
                  </ul>
                  <p>
                    In a competitive multiple-offer situation, being fully underwritten, offering strong (but genuine) earnest money, and keeping your contingencies reasonable all strengthen your position beyond just the top-line price.
                  </p>
                  <p className="text-sm text-[#4d6153]/70 mt-4 italic">
                    [Related: What actually makes an offer competitive beyond price — coming soon]
                  </p>
                </div>
              </section>

              {/* Step 6 */}
              <section id="step-6" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 6: Under Contract — Inspection & Appraisal
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <h3 className="text-xl font-serif text-[#132723] mt-6 mb-4">Inspection</h3>
                  <p>
                    {"You'll typically schedule a professional home inspection within the timeline set in your purchase agreement. Depending on findings, you can request repairs, ask for a credit, renegotiate price, or in some cases exit the contract if your inspection contingency allows it."}
                  </p>

                  <h3 className="text-xl font-serif text-[#132723] mt-8 mb-4">Appraisal</h3>
                  <p>
                    {"If you're financing, your lender requires an appraisal to confirm the home supports the loan amount. If it comes in at or above your offer price, no issue. If it comes in low — see Special Situations below."}
                  </p>
                </div>
              </section>

              {/* Step 7 */}
              <section id="step-7" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 7: Finalizing Financing
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    {"While you're under contract, your lender finalizes your loan — verifying final documentation and clearing any remaining conditions."}
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 p-4 my-6">
                    <p className="font-semibold text-yellow-800 mb-2">Important: Avoid These During This Window</p>
                    <p className="text-yellow-700 text-sm">
                      Major financial changes (new credit cards, large purchases, job changes) can affect your final approval. Keep your finances stable until closing.
                    </p>
                  </div>
                </div>
              </section>

              {/* Step 8 */}
              <section id="step-8" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 8: Closing
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    Washington closes through escrow at a title company rather than requiring an attorney. As the buyer, you can typically expect to pay:
                  </p>
                  <ul className="space-y-2 list-disc pl-5 my-6">
                    <li><strong>{"Lender's title insurance policy"}</strong> (protecting your loan, separate from the seller-paid owner&apos;s policy that protects you as the buyer)</li>
                    <li><strong>Loan origination fees</strong></li>
                    <li><strong>Appraisal and credit report fees</strong></li>
                    <li><strong>Your half of the escrow/settlement fee</strong></li>
                    <li><strong>Prepaid items</strong> — setting up your property tax and homeowners insurance escrow account, which is often a larger chunk of your total cash-to-close than people expect</li>
                  </ul>
                  <div className="bg-[#132723] text-white p-6 my-6 -mx-2">
                    <p className="font-serif text-lg mb-2">Total Buyer Closing Costs</p>
                    <p className="text-white/80">
                      In Washington, buyer closing costs typically run about 2-5% of the purchase price. Your earnest money is subtracted from this total, since it was already applied as a prepayment.
                    </p>
                  </div>
                  <p>
                    Once your loan is funded and everything is signed and recorded, you get your keys.
                  </p>
                  <p className="text-sm text-[#4d6153]/70 mt-4 italic">
                    [Related: Buyer closing costs explained — what you&apos;ll actually pay — coming soon]
                  </p>
                </div>
              </section>

              {/* Special Situations */}
              <section id="special-situations" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Special Situations
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">

                  <div className="bg-[#f0efeb] p-6 my-6">
                    <h3 className="text-lg font-serif text-[#132723] mb-3">Low Appraisal</h3>
                    <p className="text-[#4d6153] mb-0">
                      {"If the home appraises below your offer price, your lender won't lend more than that appraised value. Your options: cover the gap in cash, renegotiate the price with the seller, dispute the appraisal with additional comps, or (if your contract includes an appraisal contingency) potentially exit the deal. This is a negotiation, not automatically a dead deal."}
                    </p>
                  </div>

                  <div className="bg-[#f0efeb] p-6 my-6">
                    <h3 className="text-lg font-serif text-[#132723] mb-3">Wells & Septic Systems</h3>
                    <p className="text-[#4d6153] mb-0">
                      {"Many homes in our area use private wells and septic systems instead of city water and sewer. If you're financing, your lender will often require a well flow test and septic inspection as part of your due diligence — this is routine here, not a warning sign, but it's worth budgeting the extra time and cost into your timeline."}
                    </p>
                  </div>

                  <div className="bg-[#f0efeb] p-6 my-6">
                    <h3 className="text-lg font-serif text-[#132723] mb-3">Contingent Offers (as the Buyer)</h3>
                    <p className="text-[#4d6153] mb-0">
                      If you need to sell your current home before buying, you can write your offer contingent on that sale. This is a weaker position in a competitive market since sellers take on more uncertainty accepting it — worth a direct conversation about strategy, including whether a bridge loan or other option might make you more competitive.
                    </p>
                  </div>

                  <div className="bg-[#f0efeb] p-6 my-6">
                    <h3 className="text-lg font-serif text-[#132723] mb-3">Multiple Offer Situations</h3>
                    <p className="text-[#4d6153] mb-0">
                      In a hot market or a well-priced home, you may be competing with several other buyers. Price matters, but so does financing strength, earnest money, contingency structure, and closing timeline flexibility — all things your agent negotiates on your behalf beyond just the number on the page.
                    </p>
                  </div>

                  <p className="text-sm text-[#4d6153]/70 italic">
                    [Related: Every one of these situations gets its own full breakdown — check the links above as they go live]
                  </p>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  FAQ
                </h2>
                <div className="space-y-6">
                  <div className="border-b border-[#e2e5e2] pb-6">
                    <h3 className="font-semibold text-[#132723] mb-2">Do I need a buyer&apos;s agent, and does it cost me anything?</h3>
                    <p className="text-[#4d6153]">
                      {"You're not legally required to use one, but Washington now requires a signed buyer agency agreement before touring homes with an agent. In most transactions, buyer agent compensation is still commonly covered by the seller side, though this is negotiated deal by deal, not guaranteed."}
                    </p>
                  </div>
                  <div className="border-b border-[#e2e5e2] pb-6">
                    <h3 className="font-semibold text-[#132723] mb-2">How much do I need for a down payment?</h3>
                    <p className="text-[#4d6153]">
                      It depends on the loan type — as low as 3-3.5% for conventional and FHA programs, and often 0% for eligible VA buyers. Your specific number depends on your loan program and lender.
                    </p>
                  </div>
                  <div className="border-b border-[#e2e5e2] pb-6">
                    <h3 className="font-semibold text-[#132723] mb-2">What if I need to sell my current home before I can buy?</h3>
                    <p className="text-[#4d6153]">
                      This is common and manageable with the right strategy — worth a direct conversation about your specific situation and timeline.
                    </p>
                  </div>
                  <div className="pb-6">
                    <h3 className="font-semibold text-[#132723] mb-2">Do I need a real estate attorney to buy a home here?</h3>
                    <p className="text-[#4d6153]">
                      Not required for a standard Washington purchase, since escrow/title handles the closing documents — but one can be brought in for anything unusual in the transaction.
                    </p>
                  </div>
                </div>
              </section>

              {/* CTA Section */}
              <section className="bg-[#132723] text-white p-8 md:p-12 text-center">
                <h2 className="text-2xl md:text-3xl font-serif mb-4">Ready to Talk Specifics?</h2>
                <p className="text-white/80 mb-8 max-w-2xl mx-auto">
                  Every buyer&apos;s situation is different — this guide covers how the process generally works, but the details of your purchase deserve a direct conversation.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                  <Button
                    asChild
                    className="bg-white text-[#132723] hover:bg-[#f0efeb] px-8 py-6 text-lg font-medium"
                  >
                    <a 
                      href="https://spiveyrealtygroup.brivityidx.com/search#?q_limit=36&q_prioritize=agents.0.id=104867%7Coffice.id=5984&mlsId=101&status=1%7C3&q_sort=featured&q_offset=0" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Browse Current Listings
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </a>
                  </Button>
                  
                  <a 
                    href="tel:+13605810167"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white hover:bg-white hover:text-[#132723] transition-colors font-medium"
                  >
                    <Phone className="w-4 h-4" />
                    (360) 581-0167
                  </a>
                </div>

                <div className="flex items-center justify-center gap-4">
                  <img 
                    src={CHRIS_PHOTO} 
                    alt="Chris Pierog" 
                    className="w-12 h-12 rounded-full object-cover object-top border-2 border-white/20"
                  />
                  <div className="text-left">
                    <p className="font-medium">Chris Pierog</p>
                    <p className="text-sm text-white/60">Spivey Realty Group</p>
                  </div>
                </div>
              </section>

            </div>
          </article>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
